import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaBriefcase, FaFileContract, FaSearchDollar, FaHandshake } from 'react-icons/fa';

const CorporateAdvisoryPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Industries', path: '/industries' },
    { label: 'Corporate Advisory' }
  ];

  const services = [
    {
      icon: <FaBriefcase className="w-8 h-8 text-secondary" />,
      title: "Mergers & Acquisitions",
      description: "Strategic advisory services for all aspects of M&A transactions, from target identification and valuation to due diligence and post-merger integration."
    },
    {
      icon: <FaFileContract className="w-8 h-8 text-secondary" />,
      title: "Restructuring & Transformation",
      description: "Expert guidance for corporate restructuring, organizational transformation, and operational improvement initiatives."
    },
    {
      icon: <FaSearchDollar className="w-8 h-8 text-secondary" />,
      title: "Capital Raising",
      description: "Comprehensive support for equity and debt capital raising, including IPOs, private placements, and structured financing solutions."
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-secondary" />,
      title: "Strategic Partnerships",
      description: "Advisory services for forming strategic alliances, joint ventures, and partnerships to accelerate growth and enhance market position."
    }
  ];

  const clientTestimonials = [
    {
      quote: "Blockfirst Capital's M&A advisory team was instrumental in navigating our complex cross-border acquisition. Their strategic guidance and attention to detail ensured a successful transaction.",
      author: "Richard Branson",
      position: "CEO, Global Enterprises Inc."
    },
    {
      quote: "During our corporate restructuring, Blockfirst Capital provided exceptional insights and practical solutions that not only stabilized our operations but positioned us for long-term growth.",
      author: "Emma Rodriguez",
      position: "CFO, InnovaTech Solutions"
    },
    {
      quote: "The strategic partnership Blockfirst Capital helped us negotiate has transformed our business. Their understanding of our industry and deal-making expertise was invaluable.",
      author: "Michael Watson",
      position: "President, Quantum Industries"
    }
  ];

  return (
    <>
      <PageHeader
        title="Corporate Advisory"
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/business-people-brainstorming-office_1048944-25233953.jpg_amz21m.jpg"
      />

      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Strategic Corporate Advisory Services
              </h2>
              <p className="text-lg text-gray-700">
                Blockfirst Capital provides sophisticated corporate advisory services to help businesses navigate complex strategic decisions, capitalize on growth opportunities, and optimize their financial and operational performance.
              </p>
              <p className="text-lg text-gray-700">
                Our team of experienced professionals brings deep industry knowledge, analytical rigor, and practical expertise to deliver tailored solutions that create lasting value for our clients.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Discuss Your Strategic Needs
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/unrecognizable-businessman-pointing-printed-chart-showing-graph-colleagues-professional-content-partners-making-notes-statistics-cooperation-communication-partnership-concept_74855-14267.jpg_k9kjwo.jpg" 
                alt="Corporate Advisory" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Corporate Advisory Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We provide a comprehensive range of advisory services to help businesses make strategic decisions and execute complex transactions.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Client Success Stories
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Learn how our corporate advisory services have helped businesses achieve their strategic objectives.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {clientTestimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md"
              >
                <div className="text-4xl text-primary opacity-30 mb-4">"</div>
                <p className="text-gray-700 italic mb-6">{testimonial.quote}</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-primary">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Ready to Advance Your Business Strategy?</h3>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Our corporate advisory experts are ready to help you navigate complex decisions and capitalize on strategic opportunities.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Schedule a Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CorporateAdvisoryPage; 